import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Loader2, Pencil, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const SynopsisPanel = ({ incident, onSynopsis }) => {
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const synopsize = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/incidents/${incident.id}/synopsize`);
      onSynopsis(data);
      toast.success("Synopsis ready");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate synopsis");
    } finally {
      setGenerating(false);
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/incidents/${incident.id}`, { synopsis: text });
      onSynopsis(data);
      setEditing(false);
      toast.success("Synopsis updated");
    } catch {
      toast.error("Could not save synopsis");
    } finally {
      setSavingEdit(false);
    }
  };

  if (!incident.synopsis) {
    return (
      <div className="border border-dashed border-border bg-[#0A0A0A]/50 rounded-sm px-4 py-3 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Get an AI-written synopsis and a generated incident icon from this report's transcript and details.
        </p>
        <Button variant="outline" size="sm" onClick={synopsize} disabled={generating} data-testid="synopsify-btn"
          className="rounded-sm gap-2 text-xs shrink-0 border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 hover:text-[#007AFF]">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {generating ? "Synopsifying…" : "Synopsify"}
        </Button>
      </div>
    );
  }

  return (
    <div className="border border-[#007AFF]/30 bg-[#007AFF]/5 rounded-sm p-4" data-testid="synopsis-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#007AFF] flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> AI Synopsis
        </span>
        <div className="flex items-center gap-3">
          {!editing && (
            <button onClick={() => { setText(incident.synopsis); setEditing(true); }} data-testid="edit-synopsis-btn"
              className="text-xs text-muted-foreground hover:text-white flex items-center gap-1.5 transition-colors duration-200">
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )}
          <button onClick={synopsize} disabled={generating || editing} data-testid="resynopsify-btn"
            className="text-xs text-muted-foreground hover:text-white flex items-center gap-1.5 transition-colors duration-200 disabled:opacity-50">
            {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {generating ? "Regenerating…" : "Regenerate"}
          </button>
        </div>
      </div>
      {editing ? (
        <div>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="synopsis-edit-textarea"
            rows={7} className="bg-[#0A0A0A] border-border rounded-sm text-sm leading-relaxed resize-y focus-visible:ring-[#007AFF]" />
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" onClick={saveEdit} disabled={savingEdit || !text.trim()} data-testid="save-synopsis-btn"
              className="rounded-sm gap-1.5 h-7 text-xs bg-[#007AFF] hover:bg-[#0066d6] text-white">
              <Check className="w-3 h-3" /> {savingEdit ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={savingEdit} data-testid="cancel-synopsis-btn"
              className="rounded-sm gap-1.5 h-7 text-xs border-border">
              <X className="w-3 h-3" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm leading-relaxed" data-testid="synopsis-text">{incident.synopsis}</p>
      )}
      {!editing && incident.synopsis_generated_at && (
        <p className="text-[10px] text-muted-foreground font-mono mt-2">
          Generated {new Date(incident.synopsis_generated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
};
